import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { Eyebrow } from "@/components/Reveal";

const FILTERS = ["All", "Logos", "Branding", "Graphics", "Social", "Gaming", "Image Editing", "Video", "Motion"];

function WorkCard({ p, i }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ratio, setRatio] = useState(p.aspect || "4/3");

  const onMeta = (e) => {
    const v = e.currentTarget;
    if (v.videoWidth && v.videoHeight) setRatio(`${v.videoWidth} / ${v.videoHeight}`);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !window.matchMedia("(hover: none)").matches) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.55 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const onEnter = () => {
    const v = videoRef.current;
    if (v) {
      v.preload = "auto";
      v.play().catch(() => {});
    }
  };
  const onLeave = () => {
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="mb-6 break-inside-avoid"
    >
      <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.25 }}>
        <Link
          to={`/portfolio/${p.slug}`}
          data-testid={`portfolio-card-${p.slug}`}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          className="group relative block overflow-hidden border border-white/10 hover:border-arsenal/60 transition-colors duration-300"
        >
          <div className="overflow-hidden bg-ink-surface" style={{ aspectRatio: ratio }}>
            <img
              src={p.cover}
              alt={p.title}
              loading="lazy"
              className={`h-full w-full object-cover transition-all duration-700 group-hover:scale-105 ${playing ? "opacity-0" : "opacity-100"}`}
            />
            {p.video_url && (
              <video
                ref={videoRef}
                poster={p.cover}
                muted
                loop
                playsInline
                preload="metadata"
                onLoadedMetadata={onMeta}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${playing ? "opacity-100" : "opacity-0"}`}
                data-testid={`portfolio-video-preview-${p.slug}`}
              >
                <source src={p.video_url.replace(".mp4", ".webm")} type="video/webm" />
                <source src={p.video_url} type="video/mp4" />
              </video>
            )}
          </div>
          {p.video_url && !playing && (
            <span className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 backdrop-blur border border-white/20 text-white" aria-hidden>
              <Play className="h-4 w-4 fill-current" />
            </span>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/15 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 inset-x-0 p-6 sm:p-7 pointer-events-none">
            <div className="flex flex-wrap gap-2 mb-3">
              {p.categories.map((c) => (
                <span key={c} className="clip-tag bg-arsenal/90 px-2.5 py-1 font-mono text-[10px] tracking-[0.15em] text-white">{c.toUpperCase()}</span>
              ))}
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-white">{p.title}</h2>
            <p className="mt-1 text-sm text-slate-400">{p.client} — {p.date}</p>
          </div>
          <span className="absolute bottom-0 left-0 h-[3px] w-0 bg-arsenal transition-all duration-500 group-hover:w-full" aria-hidden />
        </Link>
      </motion.div>
    </motion.div>
  );
}

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/portfolio", { params: active === "All" ? {} : { category: active } })
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [active]);

  return (
    <div data-testid="portfolio-page" className="pt-24 sm:pt-28 relative grain">
      <Seo title="Portfolio" description="Selected work from Arsenal Artists — logos, branding, graphics, social content, gaming artwork, image editing, video and motion." path="/portfolio" image="/og-portfolio.png" />

      <div className="absolute top-0 left-1/4 h-80 w-80 rounded-full bg-arsenal/10 blur-[140px] pointer-events-none" aria-hidden />

      <header className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-10">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-mono text-[11px] sm:text-xs tracking-[0.35em] text-arsenal mb-4"
        >
          PORTFOLIO
        </motion.p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-balance">
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
              The work is <span className="text-arsenal">the point.</span>
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.25 }}
          className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed"
        >
          Every project here is real client work, delivered in the client's own style — not ours. New projects join the wall as they're completed. Hover a video card to see it move.
        </motion.p>
      </header>

      <div className="sticky top-16 sm:top-20 z-30 bg-black/80 backdrop-blur-md border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex gap-1 overflow-x-auto py-3" role="tablist" aria-label="Portfolio filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => setActive(f)}
              role="tab"
              aria-selected={active === f}
              className={`relative shrink-0 px-4 py-2 font-mono text-[11px] tracking-[0.15em] transition-colors ${
                active === f ? "text-white" : "text-slate-400 hover:text-white border border-white/10"
              }`}
            >
              {active === f && (
                <motion.span
                  layoutId="filter-pill"
                  className="absolute inset-0 bg-arsenal"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
              <span className="relative">{f.toUpperCase()}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-14">
        {loading ? (
          <div className="grid sm:grid-cols-2 gap-6">
            {[...Array(4)].map((_, i) => <div key={i} className="aspect-[4/3] bg-ink-surface animate-pulse border border-white/5" />)}
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 gap-6" data-testid="portfolio-grid">
            {items.map((p, i) => (
              <WorkCard key={p.id} p={p} i={i} />
            ))}
          </div>
        )}
        {!loading && items.length === 0 && (
          <p className="py-20 text-center text-slate-500" data-testid="portfolio-empty">Nothing in this category yet — it won't stay empty for long.</p>
        )}
      </div>

      <Marquee />

      <section className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 text-center" data-testid="portfolio-cta">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight"
        >
          Your project could be <span className="text-arsenal">next.</span>
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <Link
            to="/start-a-project"
            data-testid="portfolio-start-project"
            className="clip-btn mt-8 inline-flex items-center gap-2 bg-arsenal hover:bg-arsenal-bright text-white font-display font-bold tracking-widest text-sm px-9 py-4 transition-colors shadow-[0_0_35px_rgba(229,9,20,0.35)]"
          >
            START A PROJECT <ArrowUpRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
